import { useEffect, useState } from 'react';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { approvalsChanged, approverText, DOC_ICON, DOC_LABEL, type Approval, type ApprovalBlock } from '../lib/approvals.ts';
import { dateTime, money } from '../lib/format.ts';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Field, Skeleton } from './ui.tsx';

interface Who {
  amount: number;
  label: string;
  min_amount: number;
  approver_role: string | null;
  approver_name: string | null;
}

/**
 * Opens when a step (issue, send, pay, confirm) waits for someone's yes: says who approves, lets
 * the person ask with a note, shows a request still waiting, and the reason of a "no".
 */
export function ApprovalGate({ block, onClose, toast, fail }: { block: ApprovalBlock; onClose: () => void; toast: (text: string) => void; fail: (err: unknown) => void }) {
  const [who, setWho] = useState<Who | null>(null);
  const [req, setReq] = useState<Approval | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    action<Who | null>('studio.approver_of', { document: block.document, record: block.record }).then(setWho, fail);
    if (block.approval) action<Approval>('studio.approval', { approval: block.approval }).then(setReq, () => undefined);
  }, [block, fail]);

  const send = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      approvalsChanged();
      toast(done);
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const ask = () => void send(() => action('studio.request_approval', { document: block.document, record: block.record, note: note.trim() || null }), t('appr.sent'));
  const withdraw = () => void send(() => action('studio.withdraw_approval', { approval: block.approval }), t('appr.withdrawn_done'));

  const title = block.state === 'pending' ? t('appr.waiting_title') : block.state === 'rejected' ? t('appr.rejected_title') : t('appr.needs_title');
  const canAsk = block.state !== 'pending';
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {block.state === 'pending' && req?.mine ? (
            <Button variant="ghost" icon="undo" className="dialog-left" onClick={withdraw} disabled={busy}>
              {t('appr.withdraw')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {canAsk ? t('common.cancel') : t('common.close')}
          </Button>
          {canAsk ? (
            <Button variant="primary" icon="send" onClick={ask} busy={busy} disabled={!who}>
              {block.state === 'rejected' ? t('appr.ask_again') : t('appr.ask')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className={`appr-gate appr-${block.state}`}>
        <div className="appr-doc">
          <span className="list-icon" aria-hidden="true">
            <Icon name={DOC_ICON[block.document]} size={18} />
          </span>
          <span className="appr-doc-main">
            <span className="list-sub">{t(DOC_LABEL[block.document])}</span>
            <span className="list-title">{who ? who.label : <Skeleton w={140} />}</span>
          </span>
          <span className="appr-amount tabular">{who ? money(who.amount) : null}</span>
        </div>
        {who ? (
          <p className="appr-rule">
            <Icon name="shieldCheck" size={16} />
            <span>{t('appr.rule_line', { amount: money(who.min_amount), who: approverText(who) })}</span>
          </p>
        ) : null}
        {block.state === 'pending' ? (
          <p className="muted">
            {req ? t('appr.pending_line', { name: req.requested_by_name ?? '—', at: dateTime(req.requested_at) }) : null} {t('appr.pending_hint')}
          </p>
        ) : null}
        {block.state === 'rejected' && req ? (
          <div className="appr-reason" role="note">
            <Icon name="alert" size={16} />
            <span>
              <strong>{t('appr.rejected_by', { name: req.decided_by_name ?? '—' })}</strong> {req.reason}
            </span>
          </div>
        ) : null}
        {canAsk ? (
          <>
            {block.state === 'none' ? <p className="muted">{t('appr.needs_body')}</p> : <p className="muted">{t('appr.change_then_ask')}</p>}
            <Field label={t('appr.note')} hint={t('appr.note_hint')} counter={`${note.length}/300`}>
              <textarea className="input" rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
